import { useGLTF, useKeyboardControls } from '@react-three/drei';
import { useFrame } from '@react-three/fiber';
import { RigidBody, CuboidCollider } from '@react-three/rapier';
import { useRef, useState, useEffect, useMemo } from 'react';
import * as THREE from 'three';

const PLANE_FORWARD_SPEED = 7;
const PLANE_LIFT_FORCE = 2.5;
const MAX_VERTICAL_SPEED_UP = 5.0;
const MAX_VERTICAL_SPEED_DOWN = 6.0;
const PLANE_MAX_Y = 5;
const PLANE_MIN_Y = -10; // Límit inferior on es perd
const INITIAL_LAUNCH_IMPULSE = { x: -10, y: 5, z: 0 };
const FIXED_TIME_STEP = 1 / 60;

export function PaperPlane({ onPositionUpdate, onReady, onRingSensorIntersect, onGameOver }) {
  const planeRigidBodyRef = useRef();
  const planeVisualRef = useRef();
  const { scene } = useGLTF('./plen.glb', './draco/');
  const [gameStarted, setGameStarted] = useState(false);
  const [sub, get] = useKeyboardControls();
  const clonedScene = useMemo(() => scene.clone(), [scene]);
  const isPointerLiftingRef = useRef(false);
  const isInitialized = useRef(false);
  const accumulator = useRef(0);
  const visualWorldPosition = useMemo(() => new THREE.Vector3(), []);
  
  const [gameOverTriggered, setGameOverTriggered] = useState(false);

  useEffect(() => {
    setGameOverTriggered(false);
    setGameStarted(false);
    isInitialized.current = false;

    const handlePointerDown = () => isPointerLiftingRef.current = true;
    const handlePointerUp = () => isPointerLiftingRef.current = false;

    window.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('pointerup', handlePointerUp);

    return () => {
      window.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('pointerup', handlePointerUp);
    };
  }, []);

  useEffect(() => {
    if (planeRigidBodyRef.current && !isInitialized.current) {
      isInitialized.current = true;
      onReady?.();
    }
  }, [onReady, planeRigidBodyRef.current]);

  const triggerGameOver = () => {
    if (!gameOverTriggered && onGameOver) {
      console.log("[PaperPlane] GAME OVER sequence initiated."); // Log per confirmar
      setGameOverTriggered(true);
      onGameOver();
    }
  };

  const handleIntersection = (event) => {
    const otherColliderObject = event.other.colliderObject;
    const colliderObjectUserData = otherColliderObject ? otherColliderObject.userData : null;

    const otherRigidBodyObject = event.other.rigidBodyObject;
    const rigidBodyUserData = otherRigidBodyObject ? otherRigidBodyObject.userData : null;

    if (rigidBodyUserData && rigidBodyUserData.type === 'ringSensorRB' && typeof rigidBodyUserData.ringId !== 'undefined') {
      if (onRingSensorIntersect) {
        console.log(`[PaperPlane] Intersection with Ring Sensor RB ID: ${rigidBodyUserData.ringId}`);
        onRingSensorIntersect(rigidBodyUserData.ringId);
      }
    } else if (colliderObjectUserData && colliderObjectUserData.type === 'ringSensor' && typeof colliderObjectUserData.ringId !== 'undefined') {
      // Aquesta via potser ja no és necessària si sempre posem userData al RigidBody del sensor
      if (onRingSensorIntersect) {
        console.log(`[PaperPlane] Intersection with Ring Sensor Collider ID: ${colliderObjectUserData.ringId}`);
        onRingSensorIntersect(colliderObjectUserData.ringId);
      }
    }
  };

  const handleCollision = (event) => {
    const otherRigidBody = event.other.rigidBodyObject;
    const otherRigidBodyUserData = otherRigidBody ? otherRigidBody.userData : null;
    if (otherRigidBodyUserData && otherRigidBodyUserData.type === 'asteroid') {
      return;
    }

    triggerGameOver();
  };


  useFrame((state, delta) => {
    if (!planeRigidBodyRef.current || !planeVisualRef.current || !isInitialized.current || gameOverTriggered) return;
    
    const effectiveDelta = Math.min(delta, 0.1);
    accumulator.current += effectiveDelta;
    
    while (accumulator.current >= FIXED_TIME_STEP) {
      accumulator.current -= FIXED_TIME_STEP;
      
      const rb = planeRigidBodyRef.current;
      const { lift, down } = get();
      const isCurrentlyLifting = lift || isPointerLiftingRef.current;

      if (!gameStarted && rb.numColliders() > 0) {
        rb.applyImpulse(INITIAL_LAUNCH_IMPULSE, true);
        setGameStarted(true);
      }

      if (!gameStarted) continue;
        
      let currentLinvel = rb.linvel();
      
      if (isCurrentlyLifting) {
        const liftImpulse = PLANE_LIFT_FORCE * FIXED_TIME_STEP;
        rb.applyImpulse({ x: 0, y: liftImpulse, z: 0 }, true);
      }

      if (down) {
        const downImpulse = -PLANE_LIFT_FORCE * 0.7 * FIXED_TIME_STEP;
        rb.applyImpulse({ x: 0, y: downImpulse, z: 0 }, true);
      }
      
      currentLinvel = rb.linvel();
      let newLinvelY = THREE.MathUtils.clamp(currentLinvel.y, -MAX_VERTICAL_SPEED_DOWN, MAX_VERTICAL_SPEED_UP);
      rb.setLinvel({ x: PLANE_FORWARD_SPEED, y: newLinvelY, z: 0 }, true);

      const currentPosition = rb.translation();
      if (currentPosition.y > PLANE_MAX_Y) {
        rb.setTranslation({ ...currentPosition, y: PLANE_MAX_Y }, true);
        rb.setLinvel({ ...rb.linvel(), y: 0 }, true);
      } else if (currentPosition.y < PLANE_MIN_Y + 0.01) {
        rb.setTranslation({ ...currentPosition, y: PLANE_MIN_Y }, true);
        rb.setLinvel({ ...rb.linvel(), y: 0 }, true);
        if (gameStarted) {
            console.log("[PaperPlane] Reached PLANE_MIN_Y. Triggering GameOver.");
            triggerGameOver();
        }
      }
    }

    if (planeVisualRef.current && gameStarted) {
      const rb = planeRigidBodyRef.current;
      const currentLinvel = rb.linvel();
      
      const pitchFactor = Math.abs(currentLinvel.y) > 0.1 ? currentLinvel.y / (currentLinvel.y > 0 ? MAX_VERTICAL_SPEED_UP : MAX_VERTICAL_SPEED_DOWN) : 0;
      const targetPitchAngle = THREE.MathUtils.clamp(pitchFactor * (Math.PI * 0.25), -Math.PI * 0.25, Math.PI * 0.25);
      planeVisualRef.current.rotation.x = THREE.MathUtils.lerp(planeVisualRef.current.rotation.x, targetPitchAngle, 0.12);
    }
    
    if (onPositionUpdate && planeVisualRef.current) {
      planeVisualRef.current.getWorldPosition(visualWorldPosition);
      onPositionUpdate(visualWorldPosition);
    }
  });

  return (
    <RigidBody
      ref={planeRigidBodyRef}
      colliders={false} // Els colliders es defineixen com a fills
      mass={0.25}
      linearDamping={0.7}
      angularDamping={1.0}
      position={[0, 0, 0]}
      canSleep={false}
      enabledRotations={[false, false, false]}
      userData={{ type: 'player', objectName: 'PaperPlaneRB' }}
      onIntersectionEnter={handleIntersection}
      onCollisionEnter={handleCollision} // Aquesta és la clau
    >
      <primitive
        ref={planeVisualRef}
        object={clonedScene}
        scale={0.3}
        rotation-y={-Math.PI * 0.5}
      />
      <CuboidCollider
        args={[0.5, 0.1, 0.1]} // Mides del collider de l'avió
        position={[-0.1, 0.1, 0.25]} // Posició relativa al RigidBody de l'avió
        userData={{ objectName: 'PaperPlaneCollider' }} // userData del collider de l'avió
      />
    </RigidBody>
  );
}